import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import itemsDB from "../items.json";
import "./page.css";
import BisTables from "../components/BisTables";
// Use the same static CSS for CoT tooltips as CharacterPanel
const COT_CSS_PATH = '/Tooltip.css';

const rows = [
  ["Head","Sanctified Bloodmage Hood","Sanctified Bloodmage Hood"],
  ["Neck","Blood Queen's Crimson Choker","Blood Queen's Crimson Choker"],
  ["Shoulders","Sanctified Bloodmage Shoulderpads","Sanctified Bloodmage Shoulderpads"],
  ["Cloak","Cloak of Burning Dusk","Cloak of Burning Dusk"],
  ["Chest","Robe of the Waking Nightmare","Sanctified Bloodmage Robe"],
  ["Wrist","Bracers of Fiery Night","Bracers of Fiery Night"],
  ["Hands","Sanctified Bloodmage Gloves","Sanctified Bloodmage Gloves"],
  ["Waist","Crushing Coldwraith Belt","Crushing Coldwraith Belt"],
  ["Legs","Sanctified Bloodmage Leggings","Plaguebringer's Stained Pants"],
  ["Boots","Plague Scientist's Boots","Plague Scientist's Boots"],
  ["Ring #1","Ashen Band of Endless Destruction","Ashen Band of Endless Destruction"],
  ["Ring #2","Ring of Rapid Ascent","Ring of Rapid Ascent"],
  ["Trinket #1","Phylactery of the Nameless Lich","Dislodged Foreign Object"],
  ["Trinket #2","Charred Twilight Scale","Charred Twilight Scale"],
  ["Weapon #1","Bloodsurge, Kel'Thuzad's Blade of Agony","Bloodsurge, Kel'Thuzad's Blade of Agony"],
  ["Weapon #2","Shadow Silk Spindle","Shadow Silk Spindle"],
  ["Relic","Corpse-Impaling Spike","Corpse-Impaling Spike"],
];

export default function BISMage(){
  useEffect(()=>{ document.title = "BIS items: Mage"; },[]);

  // CharacterPanel-like tooltip logic
  const tooltipFrameRef = useRef(null);
  const [hoverPos, setHoverPos] = useState({ x:0, y:0, visible:false, content:'', height:0, width:0, itemId:null });
  const inFlightRef = useRef({});
  const cacheRef = useRef({});
  const CACHE_TTL_MS = 24*60*60*1000; // 24h
  const DEBUG = process.env.NODE_ENV === 'development';

  const unescapeJsString = useCallback((str)=>{
    if(!str) return '';
    return str
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '\r')
      .replace(/\\\//g, '/')
      .replace(/\\'/g, "'")
      .replace(/\\"/g, '"');
  }, []);

  const sanitizeCotHtml = useCallback((html)=>{
    if(!html) return '';
    let safe = html;
  safe = safe.replace(/<script[^>]*>[\s\S]*?<\/script>/gi,'')
         .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi,'')
         .replace(/<object[^>]*>[\s\S]*?<\/object>/gi,'')
         .replace(/<embed[^>]*>[\s\S]*?<\/embed>/gi,'');
    safe = safe.replace(/ on[a-z]+=["'][^"']*["']/gi,'');
    safe = safe.replace(/javascript:/gi,'');
    return safe;
  }, []);

  const ensureImagesInlined = useCallback(async (html) => {
    if(!html) return html;
    const imgRegex = new RegExp('https?://(?:cdn\\.)?cavernoftime\\.com/[\\w/.-]+\\.(?:png|gif|jpg)', 'gi');
    const matches = Array.from(new Set((html.match(imgRegex) || [])));
    if(matches.length === 0) return html;
    const results = await Promise.all(matches.map(async (url) => {
      const key = `cot_img_${url}`;
      try {
        const cached = localStorage.getItem(key);
        if(cached){
          const parsed = JSON.parse(cached);
          if(Date.now() - parsed.ts < CACHE_TTL_MS && parsed.dataUrl) return { url, dataUrl: parsed.dataUrl };
        }
      } catch(_e){}
      try {
        const proxied = url.replace(/^https?:\/\/cdn\.cavernoftime\.com\//, '/cot/cdn/');
        const resp = await fetch(proxied);
        if(!resp.ok) throw new Error('img fetch failed');
        const blob = await resp.blob();
        const dataUrl = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), dataUrl })); } catch(_e){}
        return { url, dataUrl };
      } catch(_e){ return { url, dataUrl: null }; }
    }));
    let out = html;
    results.forEach(r => { if(r.dataUrl){ out = out.split(r.url).join(r.dataUrl); } });
    return out;
  }, [CACHE_TTL_MS]);

  const parseItemHTML = useCallback((html)=>{
    let icon = null; let quality = null; let itemLevel = null; let tooltip = null;
    const iconMatchJson = html.match(/"icon"\s*:\s*"([^"]+)"/i);
    if(iconMatchJson) icon = iconMatchJson[1];
    if(!icon){
      const iconMatchJs = html.match(/(?:^|[^a-z])icon\s*:\s*['"]?([a-z0-9_-]+)['"]?/i);
      if(iconMatchJs) icon = iconMatchJs[1];
    }
    if(!icon){
      const iconCss = html.match(/icons\/(?:large|medium|tiny)\/([a-z0-9_-]+)\.\w+/i);
      if(iconCss) icon = iconCss[1];
    }
    const qualityMatch = html.match(/"quality"\s*:\s*(\d)/) || html.match(/class="q(\d)"/);
    if(qualityMatch) quality = parseInt(qualityMatch[1],10);
    const ilvlMatch = html.match(/Item Level\s*(\d{1,3})/i);
    if(ilvlMatch) itemLevel = parseInt(ilvlMatch[1],10);
    const tooltipMatch = html.match(/tooltip_enus\s*:\s*'((?:\\'|[\s\S])*?)'/i);
    if(tooltipMatch){
      tooltip = unescapeJsString(tooltipMatch[1]);
      const tableJoinRe = new RegExp('</table>\\s*(?:<br\\s*/?>)?\\s*<table', 'gi');
      const brCollapseRe = new RegExp('(?:<br\\s*/?>\\s*){2,}', 'gi');
      tooltip = tooltip.replace(tableJoinRe, '</table><table').replace(brCollapseRe, '<br/>');
    }
    return { icon, quality, itemLevel, tooltip };
  }, [unescapeJsString]);

  const queueFetch = useCallback((id)=>{
    if(inFlightRef.current[id]) return;
    const cacheKey = `cot_item_${id}`;
    // memory cache first
    try {
      const mem = cacheRef.current[cacheKey];
      if(mem && (Date.now() - mem.ts) < CACHE_TTL_MS){
        const data = mem.data;
        if (data && data.tooltip) {
          // Safe to show parsed tooltip
          setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: data.tooltip }; } return h; });
          return;
        }
        // Stale/invalid (likely full page); fall through to refetch with power=true
      }
    } catch(_e){}
    // localStorage cache
    try {
      const cached = localStorage.getItem(cacheKey);
      if(cached){
        const parsed = JSON.parse(cached);
        if(Date.now() - parsed.ts < CACHE_TTL_MS){
          cacheRef.current[cacheKey] = parsed;
          const data = parsed.data;
          if (data && data.tooltip) {
            ensureImagesInlined(data.tooltip).then(inlined => {
              setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: inlined }; } return h; });
            }).catch(()=>{
              setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: data.tooltip }; } return h; });
            });
            return;
          }
          // else stale/invalid -> refetch
        }
      }
    } catch(_e){}
    const controller = new AbortController();
    inFlightRef.current[id] = { controller };
  // Always include power=true so production NGINX returns tooltip-rich HTML
  const url = `/cot/item/${id}?power=true`;
    fetch(url, { signal: controller.signal })
      .then(r=>{ if(!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(async (html)=>{
        const parsed = parseItemHTML(html);
        const sanitized = sanitizeCotHtml(html);
        const data = { ...parsed, raw: sanitized };
        try { cacheRef.current[cacheKey] = { ts: Date.now(), data }; } catch(_e){}
        try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data })); } catch(_e){}
        const contentSrc = parsed.tooltip || sanitized || '';
        try {
          const inlined = await ensureImagesInlined(contentSrc);
          setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: inlined }; } return h; });
        } catch(_e) {
          setHoverPos(h => { if(h.visible && h.itemId === id){ return { ...h, content: contentSrc }; } return h; });
        }
      })
      .catch(e=>{ if(e.name !== 'AbortError'){ if(DEBUG) console.warn('[BIS] CoT load error', id, e); } })
      .finally(()=>{ delete inFlightRef.current[id]; });
  }, [CACHE_TTL_MS, DEBUG, ensureImagesInlined, parseItemHTML, sanitizeCotHtml]);

  function clampToViewport(x, y, w, h){
    const pad = 8;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
    const nx = Math.max(pad, Math.min(x, vw - w - pad));
    const ny = Math.max(pad, Math.min(y, vh - h - pad));
    return { x:nx, y:ny };
  }

  const handleItemEnter = (e, itemId) => {
    if(!itemId) return;
    // kick off fetch if not cached
    queueFetch(itemId);
    const content = (cacheRef.current[`cot_item_${itemId}`]?.data?.tooltip) || '';
    const provisional = { width: 360, height: 220 };
    const pos = clampToViewport(e.clientX+15, e.clientY+15, provisional.width, provisional.height);
    setHoverPos({ x: pos.x, y: pos.y, visible:true, content, width: provisional.width, height: provisional.height, itemId });
  };

  const handleItemMove = (e) => {
    setHoverPos(h => {
      if(!h.visible) return h;
      const pos = clampToViewport(e.clientX+15, e.clientY+15, h.width || 0, h.height || 0);
      if(pos.x === h.x && pos.y === h.y) return h;
      return { ...h, x: pos.x, y: pos.y };
    });
  };
  const handleItemLeave = () => setHoverPos(h=>({ ...h, visible:false, itemId:null }));

  // Write tooltip content and measure then size the iframe, same as CharacterPanel
  useEffect(()=>{
    if(tooltipFrameRef.current && hoverPos.visible){
      const iframe = tooltipFrameRef.current;
      const doc = iframe.contentDocument;
      if(doc){
        iframe.style.width = 'auto';
        iframe.style.height = 'auto';
        const raw = hoverPos.content || 'Loading...';
        const wrapped = /cavernoftime-tt/.test(raw) ? raw : `<div class="cavernoftime-tt">${raw}</div>`;
        doc.open();
        doc.write(`<html><head><meta charset="utf-8"><style>html,body{margin:0;padding:0;background:#0b0b0b;overflow:hidden}#tt-pad{display:inline-block;padding:0 1px 1px 0}</style><link rel="stylesheet" href="${COT_CSS_PATH}"></head><body><div id="tt-pad">${wrapped}</div></body></html>`);
        doc.close();
        setTimeout(()=>{
          try{
            const body = doc.body;
            if(body){
              body.style.overflow = 'hidden';
              doc.documentElement && (doc.documentElement.style.overflow = 'hidden');
              const wrapper = doc.querySelector('.cavernoftime-tt') || body;
              const rect = wrapper.getBoundingClientRect();
              const pad = 8;
              const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;
              const vh = typeof window !== 'undefined' ? window.innerHeight : 1080;
              const naturalW = Math.ceil(rect.width) + 2;
              const naturalH = Math.ceil(rect.height) + 2;
              const minH = 80;
              const finalW = Math.min(vw - 2*pad, naturalW);
              const finalH = Math.min(vh - 2*pad, Math.max(minH, naturalH));
              iframe.style.overflow = 'hidden';
              iframe.style.height = finalH + 'px';
              iframe.style.width = finalW + 'px';
              setHoverPos(h=>{ if(!h.visible) return h; const pos = clampToViewport(h.x, h.y, finalW, finalH); return { ...h, width: finalW, height: finalH, x: pos.x, y: pos.y }; });
              setTimeout(()=>{
                try{
                  const needW = Math.ceil(body.scrollWidth) + 2;
                  const needH = Math.ceil(body.scrollHeight) + 2;
                  if(needW > finalW || needH > finalH){
                    const adjW = Math.min(vw - 2*pad, Math.max(finalW, needW));
                    const adjH = Math.min(vh - 2*pad, Math.max(finalH, needH));
                    iframe.style.width = adjW + 'px';
                    iframe.style.height = adjH + 'px';
                    setHoverPos(h=>{ if(!h.visible) return h; const pos2 = clampToViewport(h.x, h.y, adjW, adjH); return { ...h, width: adjW, height: adjH, x: pos2.x, y: pos2.y }; });
                  }
                }catch(_e){}
              }, 30);
            }
          }catch(e){ if(DEBUG) console.debug('Resize failed', e); }
        }, 30);
      }
    }
  }, [hoverPos.visible, hoverPos.content, DEBUG]);

  const enriched = useMemo(() => {
    // Build a map of lookup results for each cell (skip T10)
    const map = {};
    const findBest = (name) => {
      if (!name) return null;
      const n = name.trim();
      if (!n || /^t10$/i.test(n)) return null; // ignore T10 as requested
      const nl = n.toLowerCase();
      // Prefer exact name matches, then substring matches
      const exact = itemsDB.filter(it => (it.name || '').toLowerCase() === nl);
      const candidates = exact.length ? exact : itemsDB.filter(it => (it.name || '').toLowerCase().includes(nl));
      if (!candidates || !candidates.length) return null;
      // Choose the one with highest ItemLevel (fallback to itemID)
      candidates.sort((a,b) => (Number(b.ItemLevel||b.itemLevel||0) - Number(a.ItemLevel||a.itemLevel||0)));
      const best = candidates[0];
      return {
        itemID: best.itemID || best.itemId || best.id || null,
  iconUrl: best.iconUrl || (best.icon ? `/cot/cdn/wotlk/icons/large/${best.icon}.jpg` : null),
        itemLevel: best.ItemLevel || best.itemLevel || null,
        name: best.name || n,
      };
    };

    rows.forEach(r => {
      const slot = r[0];
      map[`${slot}|fire`] = findBest(r[1]);
      map[`${slot}|arcane`] = findBest(r[2]);
    });
    return map;
  }, []);

  return (
    <main className="app-bg">
      <div className="main-container bis-page">
        <div className="bis-container">
        <h1 className="page-title">Mage BIS item list</h1>
        <p className="muted">
          This is an example best-in-slot list for Mages in Wrath of the Lich King Classic, covering Fire and Arcane specializations.
          The items listed here are for demonstration purposes only and may not represent the optimal choices for actual gameplay.
          Use this as a starting point and adjust based on your playstyle and available gear.
        </p>

        <BisTables rows={rows} specs={["Fire","Arcane"]} renderCell={(name, slot, si)=>{
            if (!name) return null;
            if (/^t10$/i.test(name)) return <span>{name}</span>;
            const rec = enriched[`${slot}|${si===0?'fire':'arcane'}`];
            if(!rec) return <span>{name}</span>;
            const href = `https://wotlk.cavernoftime.com/item=${rec.itemID}`;
            return (
              <a
                className="item-link"
                href={href}
                rel={`noopener noreferrer item=${rec.itemID}`}
                target="_blank"
                onMouseEnter={(e)=> handleItemEnter(e, rec.itemID)}
                onMouseMove={handleItemMove}
                onMouseLeave={handleItemLeave}
              >
                {rec.iconUrl ? <img src={rec.iconUrl} alt="" className="item-icon" /> : null}
                <span className="item-name">{name}</span>
              </a>
            );
          }} />
        </div>
      </div>

      {hoverPos.visible && (
        <iframe
          ref={tooltipFrameRef}
          title="bis-item-tooltip"
          style={{position:'fixed', left: hoverPos.x, top: hoverPos.y, width: hoverPos.width, height: hoverPos.height, border:'none', background:'#111', zIndex:99999, pointerEvents:'none'}}
        />
      )}
    </main>
  );
}
